import { yupResolver } from '@hookform/resolvers/yup';
import {
  useCreateStoreMutation,
  useGetStoreQuery,
  useUpdateStoreMutation,
} from 'api/client-domain/client-domain';
import { StoreResult } from 'api/client-domain/client-domain.types';
import {
  useCreateStoreOrderingSettingMutation,
  useGetStoreOrderingSettingQuery,
  useUpdateStoreOrderingSettingMutation,
} from 'api/ordering-service/ordering-service';
import { StoreOrderingForm } from 'api/ordering-service/ordering-service.types';
import { useSuspendQueries } from 'hooks/rtkq.hooks';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { InferType } from 'yup';
import { locationFormSchema } from './location-form.schema';
import { handleLocationError } from './location-form.utils';

export const useCreateLocationForm = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const form = useForm({
    resolver: yupResolver(locationFormSchema),
    defaultValues: locationFormSchema.cast({}),
  });

  const [
    createStore,
    { isLoading: isStoreLoading, isSuccess: isStoreSuccess },
  ] = useCreateStoreMutation();
  const [
    createStoreOrderingSetting,
    { isLoading: isOrderingLoading, isSuccess: isOrderingSuccess },
  ] = useCreateStoreOrderingSettingMutation();

  const submit = async data => {
    const storeResponse = await createStore(data.store).unwrap();
    await createStoreOrderingSetting({
      ...data.ordering,
      locationId: storeResponse.storeId,
      title: data.store.title,
    }).unwrap();
    navigate(location.pathname.replace(/[^/]*$/, storeResponse.storeId));
  };

  return {
    form,
    isStoreLoading,
    isOrderingLoading,
    isStoreSuccess,
    isOrderingSuccess,
    submit,
  };
};

export const useLocationForm = (locationId: string) => {
  const { t } = useTranslation();
  const form = useForm({
    resolver: yupResolver(locationFormSchema),
    defaultValues: locationFormSchema.cast({}),
  });
  const { enqueueSnackbar } = useSnackbar();

  const {
    isLoading,
    isFetching,
    isUninitialized,
    isSuccess,
    data: queryData,
  } = useSuspendQueries(
    useGetStoreQuery(locationId),
    useGetStoreOrderingSettingQuery(locationId),
  );

  const [store, ordering] = queryData;

  const data = {
    store,
    ordering,
  } as {
    store?: StoreResult;
    ordering?: StoreOrderingForm;
  };

  useEffect(() => {
    if (isSuccess)
      form.reset(
        locationFormSchema.cast({ store, ordering } as InferType<
          typeof locationFormSchema
        >),
      );
  }, [form, isSuccess, store, ordering]);

  const [updateStore, { isLoading: isStoreUpdateLoading }] =
    useUpdateStoreMutation();

  const [updateStoreOrdering, { isLoading: isOrderingUpdateLoading }] =
    useUpdateStoreOrderingSettingMutation();

  const submit = useCallback(
    async body => {
      try {
        await Promise.all([
          updateStore({ storeId: locationId, body: body.store }).unwrap(),
          updateStoreOrdering({
            locationId,
            body: { ...body.ordering, locationName: body.store.title },
          }).unwrap(),
        ]);
        enqueueSnackbar(t('snackbar.saved'), {
          severity: 'success',
        });
      } catch (e) {}
    },
    [enqueueSnackbar, locationId, t, updateStore, updateStoreOrdering],
  );

  return {
    form,
    data,
    isLoading,
    isFetching,
    isUninitialized,
    isSuccess,
    isStoreUpdateLoading,
    isOrderingUpdateLoading,
    submit,
  };
};

/**
 * Can be used if we're certain client-domain store will not be updated.
 * This will not save the store data
 */
export const useLocationOrderingForm = (locationId: string) => {
  const { t } = useTranslation();
  const form = useForm({
    resolver: yupResolver(locationFormSchema),
    defaultValues: locationFormSchema.cast({}),
  });
  const { enqueueSnackbar } = useSnackbar();

  const {
    isLoading,
    isFetching,
    isUninitialized,
    isSuccess,
    data: queryData,
  } = useSuspendQueries(
    useGetStoreQuery(locationId),
    useGetStoreOrderingSettingQuery(locationId),
  );

  const [store, ordering] = queryData;

  const data = {
    store,
    ordering,
  } as {
    store?: StoreResult;
    ordering?: StoreOrderingForm;
  };

  useEffect(() => {
    if (isSuccess)
      form.reset(
        locationFormSchema.cast({ store, ordering } as InferType<
          typeof locationFormSchema
        >),
      );
  }, [form, isSuccess, store, ordering]);

  const [updateStoreOrdering, { isLoading: isOrderingUpdateLoading }] =
    useUpdateStoreOrderingSettingMutation();

  const submit = useCallback(
    async body => {
      try {
        await updateStoreOrdering({ locationId, body: body.ordering }).unwrap();
        enqueueSnackbar(t('snackbar.saved'), {
          severity: 'success',
        });
      } catch (e) {}
    },
    [enqueueSnackbar, locationId, t, updateStoreOrdering],
  );

  return {
    form,
    data,
    isLoading,
    isFetching,
    isUninitialized,
    isSuccess,
    isOrderingUpdateLoading,
    submit,
  };
};

export const useHandleLocationError = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  return handleLocationError(enqueueSnackbar, t);
};
